import type { PropsWithChildren } from 'react';
import React, { createContext, useMemo, useState, useCallback } from 'react';
import moment from 'moment';

export const DEFAULT_WEEKS_BEFORE_TODAY = 1;
export const DEFAULT_WEEKS_AFTER_TODAY = 6;
export const DEFAULT_ZOOM_WEEKS =
  DEFAULT_WEEKS_BEFORE_TODAY + DEFAULT_WEEKS_AFTER_TODAY;
const MILLISECONDS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;

export interface TimelinePreferencesContextProps {
  currentZoomLevel: number;
  setVisibleTime: (start: number, end: number) => void;
  setZoomLevel: (weeks: number) => void;
  stepScroll: (forward: boolean) => void;
  visibleTimeEnd: number;
  visibleTimeStart: number;
}

const initialCenter = moment().valueOf();
const initialStart = moment(initialCenter)
  .subtract(DEFAULT_WEEKS_BEFORE_TODAY, 'weeks')
  .valueOf();
const initialEnd = moment(initialCenter)
  .add(DEFAULT_WEEKS_AFTER_TODAY, 'weeks')
  .valueOf();

export const TimelinePreferencesContext =
  createContext<TimelinePreferencesContextProps>({
    currentZoomLevel: DEFAULT_ZOOM_WEEKS,
    setVisibleTime: () => void null,
    setZoomLevel: () => void null,
    stepScroll: () => void null,
    visibleTimeEnd: initialEnd,
    visibleTimeStart: initialStart,
  });

/** Preserves common state for all timeline components so that we can keep the same zoom and location
 * when switching between preview and planning timelines */
export const TimelinePreferencesProvider = ({
  children,
}: PropsWithChildren<unknown>): JSX.Element => {
  const [visibleTimeStart, setVisibleTimeStart] = useState(initialStart);
  const [visibleTimeEnd, setVisibleTimeEnd] = useState(initialEnd);
  const [currentZoomLevel, setCurrentZoomLevel] = useState(DEFAULT_ZOOM_WEEKS);

  const setVisibleTime = useCallback((start: number, end: number) => {
    const weeks = (end - start) / MILLISECONDS_PER_WEEK;

    setVisibleTimeStart(start);
    setVisibleTimeEnd(end);
    setCurrentZoomLevel(weeks);
  }, []);

  const setZoomLevel = useCallback(
    (weeks: number) => {
      const center = visibleTimeStart + (visibleTimeEnd - visibleTimeStart) / 2;
      const millisecondsFromCenter = (weeks * MILLISECONDS_PER_WEEK) / 2;

      const newStart = center - millisecondsFromCenter;
      const newEnd = center + millisecondsFromCenter;

      setVisibleTime(newStart, newEnd);
    },
    [visibleTimeStart, visibleTimeEnd, setVisibleTime],
  );

  const stepScroll = useCallback(
    (forward: boolean) => {
      const timeRange = visibleTimeEnd - visibleTimeStart;
      const stepSize = timeRange * 0.3; // Step by 30% of the visible range
      const newStart = visibleTimeStart + (forward ? stepSize : -stepSize);
      const newEnd = visibleTimeEnd + (forward ? stepSize : -stepSize);
      setVisibleTime(newStart, newEnd);
    },
    [visibleTimeStart, visibleTimeEnd, setVisibleTime],
  );

  const contextValue = useMemo(
    () => ({
      currentZoomLevel,
      setVisibleTime,
      setZoomLevel,
      stepScroll,
      visibleTimeEnd,
      visibleTimeStart,
    }),
    [
      currentZoomLevel,
      setVisibleTime,
      setZoomLevel,
      stepScroll,
      visibleTimeEnd,
      visibleTimeStart,
    ],
  );

  return (
    <TimelinePreferencesContext.Provider value={contextValue}>
      {children}
    </TimelinePreferencesContext.Provider>
  );
};

export default TimelinePreferencesContext;
